/**
 * A record's own open tasks, on the record's page (wave W2).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Mahmoud's UAT of 2026-10-10: on CTR-18 "View open tasks" led to a list carrying an ITP
 * task of ANOTHER contract. The control now means THIS record's tasks: the page shows the
 * caller's open tasks about the record (and the records under it) inline, each with its
 * action, and the full list narrowed to the record is the overflow. When there is none,
 * the page says so, and the rail's control says so instead of leading nowhere.
 *
 * The narrowing is the server's (GET /api/tasks?record_type&record_id); this block draws
 * what it answers and nothing else.
 */

import { Link, useLocation } from "react-router";

import { listTasks, type Task, type TaskList, type TaskRecord } from "../api/tasks";
import { TaskDue } from "../components/TaskDue";
import { strings } from "../brand/strings";
import { FyiBlock } from "../components/FyiBlock";
import { day } from "../screens/parts";
import { recordTasksPath, taskActionPath, taskTitle } from "../screens/TasksPage";
import { useQuery, type QueryState } from "../screens/useQuery";

/** How many tasks the record page lists before pointing at the narrowed list. */
export const RECORD_TASKS_SHOWN = 5;

/** How many it reads, so the rows to act on are found among the notes. */
const RECORD_TASKS_READ = 50;

/** The caller's open tasks about one record, read once per page visit (and per stamp). */
export function useRecordTasks(
  token: string,
  record: TaskRecord | null,
  stamp = 0,
): QueryState<TaskList> {
  return useQuery(
    (signal) =>
      record === null
        ? Promise.resolve({ total: 0, open: 0, items: [] })
        : // Read past the shown rows: the notes for information must not crowd out an act.
          listTasks(token, { openOnly: true, limit: RECORD_TASKS_READ, record, signal }),
    [token, record?.type, record?.id, stamp],
  );
}

/** The rail's "View open tasks" reason when there is nothing to view, else undefined. */
export function noRecordTasksReason(state: QueryState<TaskList>, kind: string): string | undefined {
  return state.kind === "ready" && state.data.open === 0
    ? strings.recordTasks.railNone(kind)
    : undefined;
}

export function RecordTasksBlock({
  state,
  record,
  label,
  kind,
  inPageAct,
  heading,
  lede,
}: {
  readonly state: QueryState<TaskList>;
  readonly record: TaskRecord;
  /** The record's own name for the narrowed list's banner: PRJ-11, CTR-18. */
  readonly label: string;
  /** The record kind in words, for the empty line: "project", "contract". */
  readonly kind: string;
  /**
   * The page's own handler for a task whose act lives on this very page (the contract's
   * assign dialog). A link to the page it sits on would do nothing (the dead-control
   * rule), so such a task gets this handler or no button at all.
   */
  readonly inPageAct?: (task: Task) => (() => void) | undefined;
  /**
   * Wave Experience A: the bible leads with this block under its own words ("Your pending
   * actions on this project"). Absent, the record pages keep the block's own heading.
   */
  readonly heading?: string;
  readonly lede?: string;
}): React.JSX.Element {
  const copy = strings.recordTasks;
  const location = useLocation();
  return (
    <section className="q-section" id="record-tasks" data-testid="record-tasks">
      <h3 className="q-section__heading">{heading ?? copy.heading}</h3>
      <p className="q-note">{lede ?? copy.lede}</p>
      {state.kind === "loading" && (
        <p className="q-status" role="status">
          {copy.loading}
        </p>
      )}
      {(state.kind === "failed" || state.kind === "absence") && (
        <div className="q-refusal" role="alert" data-testid="record-tasks-failed">
          <p className="q-refusal__heading">{copy.failed}</p>
          <Link to={recordTasksPath(record, label)} className="q-link">
            {strings.nav.tasks}
          </Link>
        </div>
      )}
      {state.kind === "ready" && (
        <ReadyTasks
          tasks={state.data.items}
          open={state.data.open}
          record={record}
          label={label}
          kind={kind}
          here={location.pathname}
          inPageAct={inPageAct}
        />
      )}
    </section>
  );
}

/**
 * Wave Experience A, item 3: the rows to act on, then the notes for the account's
 * information in a collapsed block (the server's `actionable_by_me`).
 */
function ReadyTasks({
  tasks,
  open,
  record,
  label,
  kind,
  here,
  inPageAct,
}: {
  readonly tasks: readonly Task[];
  readonly open: number;
  readonly record: TaskRecord;
  readonly label: string;
  readonly kind: string;
  readonly here: string;
  readonly inPageAct: ((task: Task) => (() => void) | undefined) | undefined;
}): React.JSX.Element {
  const copy = strings.recordTasks;
  const actionable = tasks.filter((task) => task.actionableByMe);
  const fyi = tasks.filter((task) => !task.actionableByMe);
  const row = (task: Task): React.JSX.Element => (
    <li key={task.id} className="q-history__row" data-testid="record-task">
      <strong>{taskTitle(task.kind)}</strong> <span className="q-note">{day(task.createdAt)}</span>{" "}
      <TaskDue task={task} />
      <p className="q-thread__body" style={{ margin: "0.25rem 0" }}>
        {task.message}
      </p>
      <TaskAct task={task} here={here} inPage={inPageAct?.(task)} />
    </li>
  );
  return (
    <>
      {actionable.length === 0 ? (
        <p className="q-note" data-testid="record-tasks-empty">
          {copy.empty(kind)}
        </p>
      ) : (
        <ul className="q-history" data-testid="record-tasks-list">
          {actionable.slice(0, RECORD_TASKS_SHOWN).map(row)}
        </ul>
      )}
      <FyiBlock count={fyi.length} testId="record-tasks-fyi">
        <ul className="q-history">{fyi.map(row)}</ul>
      </FyiBlock>
      {tasks.length > 0 && (
        <Link to={recordTasksPath(record, label)} className="q-link" data-testid="record-tasks-all">
          {copy.more(open)}
        </Link>
      )}
    </>
  );
}

/** A task's act: a link elsewhere, the page's own handler, or nothing (never dead). */
function TaskAct({
  task,
  here,
  inPage,
}: {
  readonly task: Task;
  readonly here: string;
  readonly inPage: (() => void) | undefined;
}): React.JSX.Element | null {
  const path = taskActionPath(task);
  const samePage = path === here;
  if (samePage && inPage === undefined) {
    return null;
  }
  if (samePage && inPage !== undefined) {
    return (
      <button
        type="button"
        className="q-button q-mini--primary"
        data-testid="record-task-action"
        onClick={inPage}
      >
        {strings.tasks.openAction}
      </button>
    );
  }
  return (
    <Link to={path} className="q-button q-mini--primary" data-testid="record-task-action">
      {strings.tasks.openAction}
    </Link>
  );
}
